import { AsyncPipe, CommonModule, isPlatformBrowser } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	EventEmitter,
	inject,
	Input,
	input,
	model,
	OnChanges,
	OnInit,
	Output,
	output,
	PLATFORM_ID,
	signal,
	SimpleChanges,
} from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';
import dayjs from 'dayjs';
import {
	BehaviorSubject,
	distinctUntilChanged,
	filter,
	Observable,
	shareReplay,
	switchMap,
	tap,
} from 'rxjs';

import {
	AvailabilityDatesInterface,
	AvailabilityRequestInterface,
} from '@valk-nx/availability-dates-store/availability-dates.interface';
import { AvailabilityDatesService } from '@valk-nx/availability-dates-store/availability-dates.service';
import { TabMenuComponent } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu';
import { TabMenuItemDirective } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu.directive';
import { cn, fallbackLanguage } from '@valk-nx/core/lib/core';
import { ClickedModule } from '@valk-nx/core/lib/directives/clicked/clicked.module';
import {
	AccommodationTypeInterface,
	HotelInterface,
	OccupancyRestrictionsInterface,
} from '@valk-nx/core/lib/interfaces/hotels.interface';
import { AccommodationTypeModule } from '@valk-nx/core/lib/pipes/accommodation-type/accommodation-type.module';
import { BookingRedirectHelper } from '@valk-nx/helpers/lib/booking-redirect/booking-redirect.helper';
import { OccupancyInterface } from '@valk-nx/helpers/lib/interfaces/occupancy.interface';
import { toDate } from '@valk-nx/helpers/lib/transformers/toDate';
import { RouteHelper } from '@valk-nx/router-store/router.helper';

import { AvailabilityWidgetInputDateRangeComponent } from './availability-input-date-range/availability-input-date-range';
import { AvailabilityWidgetInputDropdownComponent } from './availability-input-dropdown/availability-input-dropdown';
import { AvailabilityInputDropdownItem } from './availability-input-dropdown/availability-input-dropdown.interface';
import { AvailabilityWidgetInputOccupancyComponent } from './availability-input-occupancy/availability-input-occupancy';
import {
	AvailabilityWidgetEmitInterface,
	WidgetDataActionInterface,
} from './availability-widget.component.interface';

export const occupancyRestrictions = {
	adults: {
		min: 1,
		max: 3,
	},
	children: {
		min: 0,
		max: 1,
	},
	infants: {
		min: 0,
		max: 1,
	},
	maxPersons: 2,
	maxOccupancies: 5,
};

const yesterday = dayjs().subtract(1, 'day').toDate();
type AvailabilityWidgetInput = 'hotels' | 'occupancy' | 'packages' | 'dates';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: `vp-availability-widget`,
	templateUrl: './availability-widget.component.html',
	imports: [
		AvailabilityWidgetInputDateRangeComponent,
		AvailabilityWidgetInputOccupancyComponent,
		AvailabilityWidgetInputDropdownComponent,
		AsyncPipe,
		CommonModule,
		TabMenuComponent,
		TabMenuItemDirective,
		TranslatePipe,
		AccommodationTypeModule,
		ClickedModule,
	],
})
export class AvailabilityWidgetComponent implements OnInit, OnChanges {
	private readonly availability = inject(AvailabilityDatesService);
	readonly platformId = inject(PLATFORM_ID);

	@Input() hotelGuid: string | undefined;
	hotelSlug = input<string | undefined>(undefined);
	@Input() roomGuid: string | undefined;
	@Input() dealGuid: string | undefined;
	packageGuid = input<string | undefined>(undefined);
	promotionGuid = input<string | undefined>(undefined);

	@Input() accommodationTypes: AccommodationTypeInterface[] | undefined;
	activeAccommodationTypeIndex = signal(0);
	selectedAccommodationType = model<string | undefined>(undefined);
	@Input() initialOccupancy: OccupancyInterface[] | undefined;
	@Input() defaultAdults = 2;
	@Input() occupancyRestrictions: OccupancyRestrictionsInterface =
		occupancyRestrictions;

	@Input({ transform: toDate }) initialArrivalDate: Date | undefined;
	@Input({ transform: toDate }) initialDepartureDate: Date | undefined;
	@Input() minDate = yesterday;
	@Input() maxDate = dayjs().add(1, 'year').toDate();

	@Input() hotels: AvailabilityInputDropdownItem[] = [];
	@Input() stepperConfig: {
		useStepper: boolean;
		min: number;
		max: number | undefined;
	} = { useStepper: false, min: 1, max: undefined };
	@Input() useCustomEmitter = false;

	currency = input<string>('EUR');
	showHotels = input(false);
	showOccupancy = input(true);
	showDates = input(true);
	showPrices = input(true);
	showPackages = input(false);
	showAccommodationTypes = input(false);
	packages = input<AvailabilityInputDropdownItem[]>([]);
	forceHideRangePrices = input<boolean>(false);

	openAvailabilityInNewTab = input<boolean>(false);

	@Output() customEmitter: EventEmitter<AvailabilityWidgetEmitInterface> =
		new EventEmitter<AvailabilityWidgetEmitInterface>();
	actionClick = output<{
		event: string;
		eventType: string;
		data: WidgetDataActionInterface;
	}>();
	packageSelected = output<number>();
	occupancyChanged = output<OccupancyInterface[]>();

	@Input({ required: true }) bookingtoolUrl: string;
	@Input({ required: true }) language: string = fallbackLanguage;

	occupancy: OccupancyInterface[];

	updateAvailability$ = new BehaviorSubject<AvailabilityRequestInterface>(
		undefined,
	);
	isLoading = false;
	jumpToFirstAvailableDate = true;

	availability$: Observable<AvailabilityDatesInterface>;
	currentAvailability = signal<AvailabilityDatesInterface | null>(null);
	firstDateInCalendar: string = dayjs().format('YYYY-MM-DD');
	openInput = signal<AvailabilityWidgetInput | null>(null);
	isOccupancyOpen = computed(() => this.openInput() === 'occupancy');
	isHotelsOpen = computed(() => this.openInput() === 'hotels');
	isPackagesOpen = computed(() => this.openInput() === 'packages');
	isDatePickerOpen = computed(() => this.openInput() === 'dates');
	selectedHotel: Partial<HotelInterface>;
	selectedPackage = model<AvailabilityInputDropdownItem>();
	nights = 1;

	arrivalDate: Date;
	departureDate: Date;
	_resetDateRange = 0;
	private _dateUpdateSource: 'stepper' | 'programmatic' | 'manual' | null =
		null;

	cn = cn;

	constructor() {
		effect(() => {
			const availability = this.currentAvailability();
			if (
				availability &&
				this.initialArrivalDate &&
				this.initialDepartureDate
			) {
				this.validateDatesAgainstAvailability();
				this._dateUpdateSource = null;
			}
		});

		this.availability$ = this.updateAvailability$.pipe(
			filter((request) => !!request && !!request.hotelGUID),
			distinctUntilChanged(areAvailabilityRequestsEqual),
			tap(() => (this.isLoading = true)),
			switchMap((request) =>
				this.availability.getAvailabilityDates(
					request.hotelGUID,
					request.endDate,
					request.startDate,
					request.occupancies,
					request.accommodationTypes,
					request.dealGUID,
					request.packageGUID,
					request.roomGUID,
					request.nights,
					request.promotionGUID,
				),
			),
			tap((availability) => {
				this.isLoading = false;
				this.currentAvailability.set(availability);
			}),
			shareReplay(1),
		);

		effect(() => {
			const currentPackage = this.packages()?.find(
				// TODO: Fix typing in VPL-31625
				(item) => (item.guid ?? item['GUID']) === this.packageGuid(),
			);
			/* istanbul ignore else */
			if (currentPackage) {
				this.onPackageSelect(currentPackage);
			}
		});

		// if a selectedAccommodatinTypeGuid changes, the input has been changed
		effect(() => {
			const selectedAccommodationTypeGuid =
				this.selectedAccommodationType();
			if (!selectedAccommodationTypeGuid) {
				this.activeAccommodationTypeIndex.set(0);
				return;
			}

			const selectedindex = this.accommodationTypes.findIndex(
				(accommodationType) =>
					accommodationType.guid === selectedAccommodationTypeGuid,
			);

			// we have to compensate 'All' which is at index 0
			this.activeAccommodationTypeIndex.set(selectedindex + 1);
		});

		// if the activeAccommodationType changes, an accommodationType tab as been changed
		effect(() => {
			const index = this.activeAccommodationTypeIndex();
			if (index === 0 || index > this.accommodationTypes.length) {
				// 'All'
				this.selectedAccommodationType.set(undefined);
			} else {
				this.selectedAccommodationType.set(
					this.accommodationTypes[index - 1]?.guid,
				);
			}
			this.getAvailability();
		});
	}

	ngOnChanges(changes: SimpleChanges) {
		// Track when dates are set programmatically (from @Input, e.g., URL params)
		if (changes['initialArrivalDate'] || changes['initialDepartureDate']) {
			this._dateUpdateSource = 'programmatic';
		}
	}

	ngOnInit() {
		this.nights = this.stepperConfig?.min ?? 1;

		if (this.minDate && this.minDate.toString() !== yesterday.toString()) {
			this.firstDateInCalendar = dayjs(this.minDate).format('YYYY-MM-DD');
		}

		if (
			this.initialArrivalDate &&
			this.minDate.toString() === yesterday.toString()
		) {
			this.firstDateInCalendar = dayjs(this.initialArrivalDate)
				.startOf('month')
				.format('YYYY-MM-DD');
		}

		if (!this.showOccupancy()) {
			this.occupancy = [
				{ adults: this.defaultAdults, children: 0, infants: 0 },
			];
		} else {
			this.occupancy = this.initialOccupancy;
		}

		// Validate dates against stepperConfig constraints (resolver ensures deal is loaded)
		this.validateDatesAgainstStepperConfig();
		this.setNights();
		this.getAvailability();
	}

	private validateDatesAgainstStepperConfig() {
		if (!this.hasDates() || !this.stepperConfig?.useStepper) {
			return;
		}

		const calculatedNights = this.calculateNights();
		const { min = 1, max } = this.stepperConfig;

		const isValid =
			calculatedNights >= min && (!max || calculatedNights <= max);

		if (!isValid) {
			this.clearDatesAndResetNights(min);
		}
	}

	private setNights() {
		if (this.stepperConfig?.useStepper && this.hasDates()) {
			this.nights = this.calculateNights();
		}
	}

	private validateDatesAgainstAvailability() {
		if (!this.hasDates()) {
			return;
		}

		// Skip validation for stepper and manual updates
		// But validate programmatic updates (from URL/Input) to clear invalid dates
		if (
			this._dateUpdateSource === 'stepper' ||
			this._dateUpdateSource === 'manual'
		) {
			return;
		}

		const availability = this.currentAvailability();
		if (!availability) {
			return;
		}

		const arrivalKey = dayjs(this.initialArrivalDate).format('YYYY-MM-DD');
		const departureKey = dayjs(this.initialDepartureDate).format(
			'YYYY-MM-DD',
		);

		const isAvailable = !!availability[arrivalKey]?.[departureKey];

		if (!isAvailable) {
			this.clearDatesAndResetNights();
		}
	}

	private hasDates(): boolean {
		return !!(this.initialArrivalDate && this.initialDepartureDate);
	}

	private calculateNights(): number {
		return dayjs(this.initialDepartureDate).diff(
			dayjs(this.initialArrivalDate),
			'days',
		);
	}

	private clearDatesAndResetNights(
		minNights = this.stepperConfig?.min ?? 1,
	): void {
		this.initialArrivalDate = null;
		this.initialDepartureDate = null;
		this.arrivalDate = null;
		this.departureDate = null;
		this._resetDateRange++;
		this.nights = minNights;
	}

	// Token to inform child tab menu to refresh when language or store-provided
	// accommodation type names change.
	get tabMenuRefreshToken(): string {
		const names =
			this.accommodationTypes?.map((a) => a.name).join('|') ?? '';
		return `${this.language}|${names}`;
	}

	onCtaEmit(event: Event, showPackages = false) {
		this.openInput.set(showPackages ? 'packages' : 'dates');

		this.getAvailability();

		event.stopPropagation();
	}

	onBackButton(
		event: Event,
		isHotelsOpen: boolean,
		isOccupancyOpen: boolean,
		isPackagesOpen: boolean,
		isDatePickerOpen: boolean,
	) {
		let inputToOpen: AvailabilityWidgetInput | null = null;

		if (isDatePickerOpen) inputToOpen = 'dates';
		else if (isPackagesOpen) inputToOpen = 'packages';
		else if (isOccupancyOpen) inputToOpen = 'occupancy';
		else if (isHotelsOpen) inputToOpen = 'hotels';

		this.openInput.set(inputToOpen);

		event.stopPropagation();
	}

	onDatePickerOpened(isOpen: boolean) {
		this.onInputOpenChange('dates', isOpen);
	}

	onOccupancyOpenEmit(isOpen: boolean) {
		this.onInputOpenChange('occupancy', isOpen);
	}

	onOccupancyEmit(occupancy: OccupancyInterface[]) {
		this.occupancy = occupancy;
		this.occupancyChanged.emit(occupancy);
		this.getAvailability();
	}

	onHotelSelect(hotel: Partial<HotelInterface>) {
		this.selectedHotel = hotel;
		this.getAvailability();
	}

	onPackageSelect(packageItem: AvailabilityInputDropdownItem) {
		// TODO: Fix typing in VPL-31625
		const currentPackageIndex = this.packages().findIndex(
			(item) =>
				(item.guid ?? item['GUID']) ===
				(packageItem.guid ?? packageItem.GUID),
		);

		this.selectedPackage.set(packageItem);
		this.packageSelected.emit(currentPackageIndex);
		this.resetDateRange();
		this.getAvailability();
	}

	resetDateRange() {
		this._resetDateRange++;
	}

	onInputOpenChange(input: AvailabilityWidgetInput, isOpen: boolean): void {
		const currentState = this.openInput();
		const newState = isOpen ? input : null;

		// Only update state if it's actually changing to prevent closing popover
		// when availability changes trigger change detection during month navigation
		if (currentState === newState) {
			return;
		}

		// Only close other inputs if we're opening a different input
		// This prevents flickering when the same input is already open
		if (isOpen && currentState !== input) {
			this.closeAllInputs();
		}

		this.setInputState(input, isOpen);
	}

	private closeAllInputs(): void {
		this.openInput.set(null);
	}

	private setInputState(
		input: AvailabilityWidgetInput,
		isOpen: boolean,
	): void {
		this.openInput.set(isOpen ? input : null);
	}

	recalculateAvailabilityForToday() {
		this.firstDateInCalendar = dayjs()
			.startOf('month')
			.format('YYYY-MM-DD');
		this.getAvailability();
	}

	onMonthChange(newFirstDate: string) {
		this.firstDateInCalendar = newFirstDate;
		this.jumpToFirstAvailableDate = false;
		this.getAvailability();
	}

	onStepperUpdated(nights: number) {
		this._dateUpdateSource = 'stepper';
		this.nights = nights;
		this.getAvailability();
	}

	getAvailability() {
		/* istanbul ignore else */
		if (isPlatformBrowser(this.platformId)) {
			const accommodationTypes =
				this.activeAccommodationTypeIndex() === 0
					? []
					: [
							this.accommodationTypes[
								this.activeAccommodationTypeIndex() - 1
							]?.guid,
						];
			const request: AvailabilityRequestInterface = {
				accommodationTypes,
				hotelGUID: this.hotelGuid
					? this.hotelGuid
					: this.selectedHotel?.guid,
				startDate: this.firstDateInCalendar,
				endDate: dayjs(this.firstDateInCalendar)
					.add(2, 'month')
					.format('YYYY-MM-DD'),
				occupancies: this.occupancy,
				dealGUID: this.dealGuid,
				packageGUID: this.showPackages()
					? (this.selectedPackage()?.['GUID'] ?? this.packageGuid())
					: undefined,
				roomGUID: this.roomGuid,
				promotionGUID: this.promotionGuid(),
			};

			if (this.stepperConfig?.useStepper) {
				request.nights = this.nights;
			}
			this.updateAvailability$.next(request);
		}
	}

	updateModel(event) {
		this.arrivalDate = event.arrivalDate;
		this.departureDate = event.departureDate;

		this.getAvailability();
	}

	goToBookingtool() {
		if (!this.arrivalDate || !this.departureDate) {
			this.onDatePickerOpened(true);
			return;
		}
		this.closeAllInputs();

		const accommodationTypes =
			this.activeAccommodationTypeIndex() === 0
				? undefined
				: this.accommodationTypes[
						this.activeAccommodationTypeIndex() - 1
					].accommodationType;

		if (this.useCustomEmitter) {
			this.customEmitter.emit({
				arrivalDate: this.arrivalDate,
				departureDate: this.departureDate,
				occupancy: this.occupancy,
				hotel: this.selectedHotel,
				roomGuid: this.roomGuid,
				dealGuid: this.dealGuid,
				packageGuid: this.showPackages()
					? ((this.selectedPackage()?.['GUID'] ||
							this.selectedPackage()?.guid) ??
						this.packageGuid())
					: undefined,
				accommodationType:
					this.activeAccommodationTypeIndex() === 0
						? undefined
						: this.accommodationTypes[
								this.activeAccommodationTypeIndex() - 1
							]?.accommodationType,
			});
			return;
		}

		if (this.bookingtoolUrl) {
			const bookingUrl = BookingRedirectHelper.getRedirectUrl(
				this.bookingtoolUrl,
				this.selectedHotel ? this.selectedHotel.slug : this.hotelSlug(),
				{
					roomGuid: this.roomGuid,
					dealGuid: this.dealGuid,
					packageGuid:
						(this.selectedPackage()?.['GUID'] ||
							this.selectedPackage()?.guid) ??
						this.packageGuid(),
					languageCode: this.language,
					accommodationTypes: accommodationTypes,
				},
			);

			const arrivalString = `&startDate=${dayjs(this.arrivalDate).format('YYYY-MM-DD')}`;
			const departureString = `&endDate=${dayjs(this.departureDate).format('YYYY-MM-DD')}`;
			const occupancyString = this.occupancy
				? `&occupancy=${JSON.stringify(this.occupancy)}`
				: '';

			const bookingUrlWithExtraParams = `${bookingUrl}${arrivalString}${departureString}${occupancyString}`;

			this.setEmittedData();

			if (this.openAvailabilityInNewTab()) {
				RouteHelper.redirectToExternalUrlInNewTab(
					bookingUrlWithExtraParams,
				);
			} else {
				RouteHelper.redirectToExternalUrl(bookingUrlWithExtraParams);
			}
		}
	}

	setEmittedData() {
		const eventType = () => {
			if (this.packageGuid()) {
				return 'packages';
			} else if (this.roomGuid) {
				return 'rooms';
			} else if (this.dealGuid) {
				return 'deals';
			} else {
				return 'default';
			}
		};
		const rooms = this.occupancy.map((occupancy, i) => ({
			item_name: `ROOM${i + 1}`,
			numAdults: occupancy.adults,
			numChildren: occupancy.children,
			numBabies: occupancy.infants,
		}));
		const dayTypeBooking =
			dayjs().day() === 0 || dayjs().day() === 6 ? 'weekend' : 'weekday';
		const dayTypeVisit =
			dayjs(this.arrivalDate).day() === 5 ||
			dayjs(this.arrivalDate).day() === 6
				? 'weekend'
				: 'weekday';

		this.actionClick.emit({
			event: 'search_availability',
			eventType: eventType(),
			data: {
				dayTypeBooking,
				dayTypeVisit,
				dealGuid: this.dealGuid,
				hotelGuid: this.hotelGuid,
				leadtime: dayjs(this.arrivalDate).diff(
					dayjs().format('YYYY-MM-DD'),
					'day',
				),
				numGuests: this.occupancy.reduce((acc, curr) => {
					return acc + curr.adults + curr.children + curr.infants;
				}, 0),
				numRooms: this.occupancy.length,
				numNights: dayjs(this.departureDate).diff(
					dayjs(this.arrivalDate),
					'days',
				),
				packageGuid:
					(this.selectedPackage()?.['GUID'] ||
						this.selectedPackage()?.guid) ??
					this.packageGuid(),
				roomGuid: this.roomGuid,
				rooms,
				selectedArrivalDate: dayjs(this.arrivalDate).format(
					'YYYY-MM-DD',
				),
				selectedDepartureDate: dayjs(this.departureDate).format(
					'YYYY-MM-DD',
				),
			},
		});
	}
}

/**
 * Compares two AvailabilityRequestInterface objects for equality,
 * used primarily for the distinctUntilChanged operator.
 * It performs a shallow comparison of most properties and a deep comparison
 * for `occupancies` and `accommodationTypes` arrays using JSON.stringify.
 * @param prev The previous AvailabilityRequestInterface object.
 * @param curr The current AvailabilityRequestInterface object.
 * @returns True if the requests are considered equal, false otherwise.
 */
export function areAvailabilityRequestsEqual(
	prev: AvailabilityRequestInterface,
	curr: AvailabilityRequestInterface,
): boolean {
	if (!prev || !curr) return false;
	const shallowEqual =
		prev.hotelGUID === curr.hotelGUID &&
		prev.startDate === curr.startDate &&
		prev.endDate === curr.endDate &&
		prev.dealGUID === curr.dealGUID &&
		prev.packageGUID === curr.packageGUID &&
		prev.roomGUID === curr.roomGUID &&
		prev.nights === curr.nights &&
		prev.promotionGUID === curr.promotionGUID;
	const occupanciesEqual =
		JSON.stringify(prev.occupancies) === JSON.stringify(curr.occupancies);
	const accommodationTypesEqual =
		JSON.stringify(prev.accommodationTypes) ===
		JSON.stringify(curr.accommodationTypes);
	return shallowEqual && occupanciesEqual && accommodationTypesEqual;
}
